import { createFileRoute, useParams } from '@tanstack/react-router'
import { Alert, AlertIcon, Box, Flex } from '@chakra-ui/react'
import { useState } from 'react'
import { ParsedUrlQuery } from 'querystring'
import useAppSelector from '@hooks/useAppSelector'
import MediaSoupCall from '@features/commentary/components/Participant/MediaSoupCall'
import { SessionEndScreen } from '@features/commentary/components/PublicParticipant/SessionEndScreen'
import { VideoControlsAndPreview } from '@features/commentary/components/VideoControlsAndPreview'
import { TwoColumnLayout } from '@components/Layouts/TwoColumnLayout'
import useAppDispatch from '@hooks/useAppDispatch'
import {
  LobbyConnectionState,
  selectConnectionState,
  setConnectionState,
} from '@features/lobby/slice'
import { ConnectionStateEnum } from '@services/remoteMedia/useWebRTCMediaServer/mediasoupOrchestrator/ConnectionState'
import { AuthenticationContainer } from '@features/lobby/components/AuthenticationContainer'
import { MediaTrackSettings } from '@features/commentary/components/DeviceSelector'
import {
  UserMediaProvider,
  useUserMediaContext,
} from '@providers/UserMediaProducer'

const LobbyPage = ({ token, projectId }) => {
  const [lobbyCredentials, setLobbyCredentials] = useState<null | {
    token: string
    hermesRouterAddress: string
    workflowSessionId: string
  }>(null)
  const lobbyConnectionState = useAppSelector(selectConnectionState)
  const { stream, error, refresh } = useUserMediaContext()
  const dispatch = useAppDispatch()

  const isTryingToAuthenticate = [
    LobbyConnectionState.RequiresAuth,
    ConnectionStateEnum.Idle,
    ConnectionStateEnum.Authenticating,
    ConnectionStateEnum.Authenticated,
    LobbyConnectionState.Inactive,
  ].some((connectionState) => connectionState === lobbyConnectionState)

  if (
    LobbyConnectionState.Joined === lobbyConnectionState &&
    lobbyCredentials !== null
  ) {
    return (
      <MediaSoupCall
        workflowSessionId={lobbyCredentials?.workflowSessionId}
        handleLeave={() => {
          dispatch(setConnectionState(LobbyConnectionState.Left))
        }}
        lobbyCredentials={lobbyCredentials}
      />
    )
  }

  if (LobbyConnectionState.Left === lobbyConnectionState) {
    return (
      <SessionEndScreen
        handleClick={() => {
          refresh()
          dispatch(setConnectionState(ConnectionStateEnum.Idle))
        }}
      />
    )
  }

  if (ConnectionStateEnum.Error === lobbyConnectionState) {
    return <Box>Failed</Box>
  }

  return (
    <TwoColumnLayout
      left={
        <Flex direction={'column'} justify={'center'} align={'center'}>
          {error && (
            <Alert status="warning">
              <>
                <AlertIcon />
                {error}
              </>
            </Alert>
          )}
          {stream && <VideoControlsAndPreview mediaStream={stream} />}
          {!stream && (
            <MediaTrackSettings aria-label="Device Settings" size={'lg'} />
          )}
        </Flex>
      }
      right={
        isTryingToAuthenticate && (
          <AuthenticationContainer
            token={token}
            projectId={projectId}
            setLobbyCredentials={setLobbyCredentials}
          />
        )
      }
    />
  )
}

const Lobby = ({ token, projectId }) => {
  const { videoDeviceId, audioDeviceId, videoHeight } = useAppSelector(
    (state) => state.commentary
  )

  return (
    <UserMediaProvider
      constraints={{
        video: videoDeviceId
          ? {
              deviceId: { exact: videoDeviceId },
              height: videoHeight,
              aspectRatio: 16 / 9,
              frameRate: { min: 10, ideal: 30 },
            }
          : {
              height: videoHeight,
              aspectRatio: 16 / 9,
            },
        audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
      }}
    >
      <LobbyPage token={token} projectId={projectId} />
    </UserMediaProvider>
  )
}

const LobbyRoute = () => {
  const { projectId, token } = useParams({
    from: '/lobby/$projectId/$token',
  })

  return <Lobby token={token} projectId={projectId} />
}

export const Route = createFileRoute('/lobby/$projectId/$token')({
  component: LobbyRoute,
})
